import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FaUpload, FaSpinner, FaCheck, FaTimes } from 'react-icons/fa';

const UploadTest = () => {
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState('');

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setStatus('');
    }
  };

  const testUpload = async () => {
    if (!selectedFile || !user) {
      setStatus('❌ Please select a file and ensure you are logged in');
      return;
    }

    try {
      setUploading(true);
      setStatus('🔄 Testing upload...');

      // Debug: Log user info
      console.log('User info:', {
        uid: user.uid,
        email: user.email,
        isAuthenticated: !!user
      });

      // Test with a simple path first
      const testRef = ref(storage, `pdf-pages/${user.uid}/test_upload.txt`);
      const testBlob = new Blob(['Test upload'], { type: 'text/plain' });
      
      setStatus('🔄 Uploading test file...');
      console.log('Uploading to path:', `pdf-pages/${user.uid}/test_upload.txt`);
      
      await uploadBytes(testRef, testBlob);
      
      setStatus('🔄 Getting download URL...');
      const downloadURL = await getDownloadURL(testRef);
      
      setStatus(`
        ✅ Test upload successful!
        🔗 URL: ${downloadURL}
        👤 User: ${user.email}
        🆔 UID: ${user.uid}
        📍 Path: pdf-pages/${user.uid}/test_upload.txt
      `);

    } catch (error) {
      console.error('Upload test error:', error);
      setStatus(`❌ Upload test failed: ${error.message}
      
Error details:
- Code: ${error.code}
- Message: ${error.message}
- User UID: ${user.uid}
- User Email: ${user.email}
- Storage Path: pdf-pages/${user.uid}/test_upload.txt`);
    } finally {
      setUploading(false);
    }
  };

  const testImageUpload = async () => {
    if (!selectedFile || !user) {
      setStatus('❌ Please select an image file and ensure you are logged in');
      return;
    }

    try {
      setUploading(true);
      setStatus('🔄 Testing image upload...');

      // Debug: Show Firebase config
      console.log('🔍 Firebase Config:', {
        projectId: storage.app.options.projectId,
        storageBucket: storage.app.options.storageBucket,
        authDomain: storage.app.options.authDomain
      });

      const extension = selectedFile.name.split('.').pop().toLowerCase();
      const imageRef = ref(storage, `pdf-pages/${user.uid}/test_image.${extension}`);
      
      setStatus('🔄 Uploading image...');
      await uploadBytes(imageRef, selectedFile);
      
      setStatus('🔄 Getting download URL...');
      const downloadURL = await getDownloadURL(imageRef);
      
      setStatus(`
        ✅ Image upload successful!
        📁 File: ${selectedFile.name}
        🔗 URL: ${downloadURL}
        👤 User: ${user.email}
        🆔 UID: ${user.uid}
        🗂️ Storage Bucket: ${storage.app.options.storageBucket}
        🏢 Project ID: ${storage.app.options.projectId}
      `);

    } catch (error) {
      console.error('Image upload test error:', error);
      setStatus(`❌ Image upload test failed: ${error.message}\n\nConfig:\n- Bucket: ${storage.app.options.storageBucket}\n- Project: ${storage.app.options.projectId}`);
    } finally {
      setUploading(false);
    }
  };

  if (!user) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <FaTimes className="text-red-500 text-2xl mx-auto mb-2" />
        <p className="text-red-700">Please log in to test uploads</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-amber-800 mb-2">
          🔧 Upload Test
        </h2>
        <p className="text-amber-600">
          Test Firebase Storage permissions
        </p>
      </div>

      <div className="mb-4">
        <input
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="block w-full text-sm text-amber-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
        />
      </div>

      <div className="space-y-3">
        <button
          onClick={testUpload}
          disabled={uploading}
          className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
        >
          {uploading ? (
            <>
              <FaSpinner className="animate-spin" />
              Testing...
            </>
          ) : (
            <>
              <FaUpload />
              Test Basic Upload
            </>
          )}
        </button>

        <button
          onClick={testImageUpload}
          disabled={uploading || !selectedFile}
          className="w-full bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
        >
          {uploading ? (
            <>
              <FaSpinner className="animate-spin" />
              Testing...
            </>
          ) : (
            <>
              <FaUpload />
              Test Image Upload
            </>
          )}
        </button>
      </div>

      {status && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${
          status.includes('✅') 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : status.includes('❌') 
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-blue-50 text-blue-700 border border-blue-200'
        }`}>
          <div dangerouslySetInnerHTML={{ __html: status.replace(/\n/g, '<br>') }} />
        </div>
      )}

      <div className="mt-4 text-xs text-amber-600">
        <p><strong>User Info:</strong></p>
        <p>Email: {user.email}</p>
        <p>UID: {user.uid}</p>
      </div>
    </div>
  );
};

export default UploadTest;
